<template>
    <div id="app">
        <notifications/>

        <el-progress
            v-if="isContentLoadingOnMainPage"
            class="progress-bar"
            :percentage="contentLoadingPercentage"
            text-inside
        />

        <fade-transition
            mode="out-in"
            :duration="100"
        >
            <router-view
                v-loading.fullscreen.lock="isContentLoading"
                element-loading-background="rgba(0, 0, 0, 0.2)"
            />
        </fade-transition>
    </div>
</template>

<script>
import globalMixin from '@/mixins/globalMixin'
import pageSizeMixin from '@/mixins/pageSizeMixin'
import {defaultFontSize} from '@/enum/defaultDashboardSettings'
import {version} from '../package.json';
import {objectKeysOnlyStrings} from "@/util/objectUtil";
import {Progress} from 'element-ui'

const context = new AudioContext();
let gainNode = context.createGain();
let source = null

export default {
    mixins: [globalMixin, pageSizeMixin],
    components: {
        [Progress.name]: Progress,
    },
    data() {
        return {
            settingNewColors: false,
            playing: false,
            timer: null,
            appRenderedLogFinish: null,
            contentLoadingPercentage: 0
        }
    },
    provide() {
        const appData = {}

        Object.defineProperty(appData, 'settingNewColors', {
            enumerable: true,
            get: () => this.settingNewColors,
        })

        return {
            appData,
        }
    },
    async created() {
        this.appRenderedLogFinish = this.debugLog('App Rendered in')
        await this.$store.dispatch('dashboards/setContentLoading', true)

        const dataLoadedLogFinish = this.debugLog('All Dashboard Data Loaded in')

        document.title = `Realtime Monitor v${version}`

        try {
            this.contentLoadingPercentage = 2

            const getEntitiesListFinish = this.debugLog('Got entities list in')
            await this.$store.dispatch('entities/getEntitiesList', true)
            getEntitiesListFinish()
            this.contentLoadingPercentage = 18

            try {
                const getLanguagesFinish = this.debugLog('Got languages in')
                await this.$store.dispatch('lang/getLanguages', true)
                getLanguagesFinish()
                this.contentLoadingPercentage = 20
            } finally {}

            const setLanguageFinish = this.debugLog('Set language in')
            const activeLanguage = this.$store.getters['lang/getActiveLanguage'] || 'en'

            await this.$store.dispatch('lang/setLanguage', {
                lang: activeLanguage,
                loadCached: true
            })

            if (activeLanguage && process.env.NODE_ENV === 'production') {
                const locale = activeLanguage.locale
                this.$i18n.locale = locale
                this.$i18n.mergeLocaleMessage(locale, objectKeysOnlyStrings(this.$store.state.lang.translations))
            }
            setLanguageFinish()
            this.contentLoadingPercentage = 23

            const getDashboardsFinish = this.debugLog('Got dashboards in')
            await this.$store.dispatch('dashboards/getDashboards', true)
            getDashboardsFinish()
            this.contentLoadingPercentage = 27

            const getAllTemplatesCategoryFinish = this.debugLog('Got templates and categories in')
            await this.$store.dispatch('templatesCategory/getAllTemplatesCategory', true)
            getAllTemplatesCategoryFinish()

            this.contentLoadingPercentage = 41

            const selectDashboardFinish = this.debugLog('Selected dashboard in')
            const activeDashboardID = this.activeDashboard ? this.activeDashboard.DashboardID : null

            if (activeDashboardID === null) {
                await this.$store.dispatch('dashboards/selectDashboard')
            } else {
                const dashboardExist = this.allDashboards.find(dashboard => {
                    if (!dashboard || activeDashboardID === null) {
                        return false
                    }

                    return dashboard.DashboardID === activeDashboardID
                })

                if (dashboardExist) {
                    await this.$store.dispatch(
                        'dashboards/selectDashboard',
                        {
                            dashboard: dashboardExist,
                            loadCached: true
                        }
                    )
                } else {
                    await this.$store.dispatch('dashboards/selectDashboard', {
                        dashboard: this.allDashboards[this.allDashboards.length - 1],
                        loadCached: true
                    })
                }
            }
            selectDashboardFinish()
            this.contentLoadingPercentage = 68

            const setActiveLayoutFinish = this.debugLog('Setup active layout in')
            await this.$store.dispatch('layout/setupActiveLayout', true)
            setActiveLayoutFinish()
            this.contentLoadingPercentage = 71

            const getAllTemplateDictionariesFinish = this.debugLog('Got all template dictionaries in')
            await this.$store.dispatch('templatesCategory/getAllTemplateDictionaries', true)
            this.contentLoadingPercentage = 80

            getAllTemplateDictionariesFinish()

            this.contentLoadingPercentage = 100
            await this.$store.dispatch('layout/setFavicon')
        } finally {
            await this.$store.dispatch('dashboards/setContentLoading', false)

            dataLoadedLogFinish()
        }
    },
    computed: {
        layout() {
            return this.$store.getters['layout/getActiveLayout']
        },
        colors() {
            return this.$store.getters['layout/colors']('activeLayout')
        },
        fontSize() {
            return this.$store.getters['layout/baseFontSize']('activeLayout')
        },
        activeDashboard() {
            return this.$store.getters['dashboards/getActiveDashboard']
        },
        allDashboards() {
            return this.$store.getters['dashboards/getAllDashboards']
        },
        isContentLoading() {
            return this.$store.state.dashboards.contentLoading && window.location.pathname !== '/'
        },
        isContentLoadingWithoutPathChecking() {
            return this.$store.state.dashboards.contentLoading
        },
        isContentLoadingOnMainPage () {
            return this.$store.state.dashboards.contentLoading && window.location.pathname === '/'
        },
        sounds() {
            return this.$store.getters['sound/getSounds']
        },
        soundsLength() {
            return this.$store.getters['sound/getSoundsLength']
        },
        isMuted() {
            return this.$store.getters['sound/isMuted']
        },
        getVolume() {
            return this.$store.getters['sound/getVolume']
        }
    },
    methods: {
        debugLog(message) {
            const time = Date.now()

            return function () {
                const diff = Date.now() - time
                console.log(message, Math.round(diff / 1000), 's', `(${diff}ms)`)
                return diff
            }
        },
        initMainColorsVars(colors) {
            this.settingNewColors = true
            for (let color in colors) {
                document.documentElement.style.setProperty('--' + color + '-color', colors[color]);
            }

            this.$nextTick(() => {
                this.settingNewColors = false
            })
        },
        initMainFontSizeVar(fontSize) {
            fontSize = fontSize || defaultFontSize
            document.documentElement.style.setProperty('--font-size-xs', (fontSize - 4) + 'px');
            document.documentElement.style.setProperty('--font-size-sm', (fontSize - 2) + 'px');
            document.documentElement.style.setProperty('--font-size-base', fontSize + 'px');
            document.documentElement.style.setProperty('--font-size-lg', (fontSize + 2) + 'px');
            document.documentElement.style.setProperty('--font-size-xl', (fontSize + 4) + 'px');
            document.documentElement.style.setProperty('--font-size-2xl', (fontSize + 6) + 'px');
            document.documentElement.style.setProperty('--font-size-3xl', (fontSize + 12) + 'px');
        },
        updateSoundState(sound) {
            this.$store.dispatch('sound/updateSoundState', sound)
        },
        updateSoundPlayingState({sound, soundState}) {
            this.$store.dispatch('sound/updateSoundPlayingState', {
                sound: sound,
                soundState: soundState
            })
        },
        playSound(sound) {
            if (!sound) {
                return
            }
            this.playing = true

            this.updateSoundPlayingState({
                sound: sound,
                soundState: true
            })

            const playNextSound = () => {
                if (!this.sounds) {
                    return
                }

                const soundsToPlay = this.sounds.filter(el => !el.played)
                if (soundsToPlay && soundsToPlay.length && !this.isMuted) {
                    this.playSound(soundsToPlay[0])
                }
                if (soundsToPlay.length === 0) {
                    this.playing = false
                }
            }
            const play = (audioBuffer) => {
                source = context.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(gainNode)
                gainNode.connect(context.destination);
                source.start(context.currentTime);
                const self = this
                source.onended = function () {
                    self.timer = null
                    self.updateSoundState(sound)
                    setTimeout(() => {
                        self.updateSoundPlayingState({
                            sound: sound,
                            soundState: false
                        })
                        playNextSound()
                    }, 1000)
                };
            }
            this.timer = setTimeout(() => {
                window.fetch(sound.sound)
                    .then(response => response.arrayBuffer())
                    .then(arrayBuffer => context.decodeAudioData(arrayBuffer))
                    .then(audioBuffer => {
                        play(audioBuffer)
                    })
            }, 0)
        }
    },
    watch: {
        colors: {
            deep: true,
            immediate: true,
            handler: function (values) {
                if (!values) {
                    return
                }
                this.initMainColorsVars(values)
            },
        },
        fontSize: {
            immediate: true,
            handler: function (value) {
                this.initMainFontSizeVar(value)
            },
        },
        activeDashboard: {
            immediate: true,
            deep: true,
            handler: async function (oldVal, newVal) {
                this.$store.dispatch('sound/clearSoundsQueue')
                if (oldVal && newVal && oldVal.DashboardID !== newVal.DashboardID) {
                    await this.$store.dispatch('layout/setupActiveLayout')
                }
            }
        },
        isMuted(val) {
            if (val) {
                gainNode.gain.value = 0;
                gainNode.gain.setValueAtTime(0, context.currentTime);
                this.playing = false
                clearTimeout(this.timer)
            }
            if (!val && this.soundsLength > 0 && !this.playing) {
                if (!this.sounds) {
                    return
                }

                gainNode.gain.value = Number(this.getVolume);
                gainNode.gain.setValueAtTime(Number(this.getVolume), context.currentTime);
                const soundsToPlay = this.sounds.filter(el => !el.played)
                if (soundsToPlay && soundsToPlay.length) {
                    this.playSound(soundsToPlay[0])
                }
            }
        },
        getVolume(val) {
            gainNode.gain.value = Number(val)
        },
        soundsLength(newVal, oldVal) {
            if (!oldVal || !this.sounds) {
                return
            }

            const soundsToPlay = this.sounds.filter(el => !el.played)
            if (newVal > oldVal && soundsToPlay && soundsToPlay.length && !this.isMuted && !this.playing) {
                if (soundsToPlay) {
                    this.playSound(soundsToPlay[0])
                }
            }
        },
        '$route.name': {
            handler() {
                gainNode.gain.value = 0;
                gainNode.gain.setValueAtTime(0, context.currentTime);
                this.playing = false
                clearTimeout(this.timer)
            }
        }
    },
    beforeDestroy() {
        this.$store.dispatch('sound/clearSoundsQueue')
    },
    mounted() {
        if (this.appRenderedLogFinish) {
            this.appRenderedLogFinish()
        }

        const timeout = 8 * 60 * 60 * 1000 // 8 hours

        setTimeout(() => {
            window.location.reload()
        }, timeout)
    }
}
</script>

<style lang="scss">
[dir="rtl"] .popper__arrow::after {
    display: none !important;
}
.list-popover-height .max-w-lg, .list-popover-height .max-w-xs {
    max-height: 300px !important;
    overflow-y: auto;
}
.el-color-picker__panel {
  background-color: var(--secondaryBgColor-color) !important;
}
.el-color-picker__panel .el-input__inner {
    background: var(--mainBgColor-color);
    color: var(--mainTextColor-color);
}
.el-popover {
    color: var(--mainTextColor-color) !important;
    background: var(--mainBgColor-color) !important;
}
.el-input__inner {
    background: var(--mainBgColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-select-dropdown__item {
    color: var(--mainTextColor-color) !important;
}
.el-button.el-button--default {
    background: var(--itemsMainColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-input-number__decrease, .el-input-number__increase {
    background: var(--itemsSecondaryColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
 .el-input-number .el-input__inner {
    background: var(--mainBgColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.highcharts-label text {
    color: var(--mainTextColor-color) !important;
}
.el-checkbox__input.is-checked + span.el-checkbox__label, .el-checkbox__label {
    color: var(--mainTextColor-color) !important;
}
.el-range-input {
    background: var(--mainBgColor-color);
    color: var(--mainTextColor-color) !important;
}
.el-picker-panel__body {
    background: var(--mainBgColor-color);
}
.el-form-item__label {
    color: var(--mainTextColor-color) !important;
}
.el-tag, .el-tag.el-tag--info {
    background: var(--itemsMainColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-select-dropdown__item.is-disabled:hover {
    background: var(--secondaryBgColor-color) !important;
}
.el-range-separator {
    color: var(--mainTextColor-color) !important;
}
.el-date-range-picker__header, .el-picker-panel__icon-btn {
    color: var(--mainTextColor-color) !important;
}
.el-date-table th {
    color: var(--mainTextColor-color) !important;
}
.el-date-table td.disabled div {
    background-color: transparent !important;
}
.el-date-table th {
    background-color: var(--secondaryBgColor-color) !important;
}
.el-dropdown-menu {
    background-color: var(--secondaryBgColor-color) !important;
}
.redactor-box.redactor-styles-on {
    background: var(--mainBgColor-color) !important;
}
tspan {
    color: var(--mainTextColor-color) !important;
    fill: var(--mainTextColor-color) !important;
}
body {
    background: var(--secondaryBgColor-color);
}
.el-select-dropdown__item.hover, .el-select-dropdown__item:hover {
    background-color: var(--mainBgColor-color) !important;
}
.el-select-dropdown.is-multiple .el-select-dropdown__item.selected {
    background: var(--secondaryBgColor-color) !important;
}
.el-time-spinner__item, .el-time-range-picker__header {
    color: var(--mainTextColor-color) !important;
}
.el-select-dropdown__empty {
    background: var(--mainBgColor-color) !important;
    color: var(--mainTextColor-color) !important;
}
.el-time-spinner {
    background: var(--secondaryBgColor-color) !important;
}
.el-time-spinner__item:hover {
    background: var(--mainBgColor-color) !important;
}
.el-time-panel__content::after, .el-time-panel__content::before {
    border-top: 1px solid var(--mainTextColor-color) !important;
    border-bottom: 1px solid var(--mainTextColor-color) !important;
    z-index: 2 !important;
}
.el-select-dropdown {
    background-color: var(--secondaryBgColor-color) !important;
}
.el-notification {
    background-color: var(--mainBgColor-color) !important;
}
.el-notification__content, .el-notification__title {
    color: var(--mainTextColor-color) !important;
}
.el-dropdown-menu__item {
    color: var(--mainTextColor-color) !important;
}
.el-dropdown-menu__item:focus, .el-dropdown-menu__item:not(.is-disabled):hover {
    background-color: var(--mainBgColor-color) !important;
}
.el-message-box {
    background-color: var(--mainBgColor-color) !important;
}.el-message-box__message, .el-message-box__title {
    color: var(--mainTextColor-color) !important;
}

 .progress-bar {
     padding: 20px;

     .el-progress-bar__innerText {
         display: none;
     }
 }
</style>
